import type { PaginatedResponse } from 'sama-shared';
import { baseApi } from 'sama-shared';
import type { AdminRole, AdminUser } from '../types/admin';
import type { GetAdminrolesParams, GetPartnerUsersParams } from '../types/api';

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPartnerUsers: builder.query<PaginatedResponse<AdminUser>, GetPartnerUsersParams>({
      query: (params) => ({
        url: '/admin/user',
        method: 'GET',
        params,
      }),
      // Proporciona tags granulares por partnerId y un tag general 'User'
      providesTags: (_result, _error, params) => [
        { type: 'User', id: params.partnerId || 'LIST' },
        'User',
      ],
    }),

    registerPartnerUser: builder.mutation<
      void,
      {
        partnerId: string;
        name: string;
        email: string;
        password: string;
        roles: { partnerLocationId: string | null; roleId: string }[];
      }
    >({
      query: (body) => ({
        url: '/admin/user', // Asumiendo que esta es tu ruta de registro
        method: 'POST',
        body,
      }),
      // Invalida la lista del partner afectado para forzar el refetch automático
      invalidatesTags: (_result, _error, { partnerId }) => [
        { type: 'User', id: partnerId },
        'User',
      ],
    }),

    updatePartnerUser: builder.mutation<
      void,
      {
        id: string;
        partnerId?: string;
        name?: string;
        email?: string;
        password?: string;
        roles?: { partnerLocationId: string | null; roleId: string }[];
      }
    >({
      query: ({ id, partnerId: _, ...body }) => ({
        url: `/admin/user/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { partnerId }) => [
        { type: 'User', id: partnerId || 'LIST' },
        'User',
      ],
    }),

    deletePartnerUser: builder.mutation<void, { id: string; partnerId?: string }>({
      query: ({ id }) => ({
        url: `/admin/user/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { partnerId }) => [
        { type: 'User', id: partnerId || 'LIST' },
        'User',
      ],
    }),

    getRoles: builder.query<PaginatedResponse<AdminRole>, GetAdminrolesParams>({
      query: (params) => ({
        url: '/admin/role',
        method: 'GET',
        params,
      }),
      providesTags: ['Role'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPartnerUsersQuery,
  useRegisterPartnerUserMutation,
  useUpdatePartnerUserMutation,
  useDeletePartnerUserMutation,
  useGetRolesQuery,
} = usersApi;
